import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { firestoreDb, hasFirebaseConfig } from './firebase';
export { hasFirebaseConfig } from './firebase';

export type LeaderboardGameId = 'tetris' | 'snake' | 'pacman' | 'brickbreaker';

export interface LeaderboardEntry {
  name: string;
  score: number;
}

export const PLAYER_NAME_STORAGE_KEY = 'leaderboard-player-name';

export function sanitizePlayerName(rawName: string): string {
  return rawName.replace(/[^a-zA-Z0-9 _-]/g, '').trim();
}

export function isValidPlayerName(name: string): boolean {
  return name.length >= 1 && name.length <= 8;
}

export function getSavedPlayerName(): string {
  const value = localStorage.getItem(PLAYER_NAME_STORAGE_KEY) || '';
  return sanitizePlayerName(value).slice(0, 8);
}

export function savePlayerName(name: string): void {
  localStorage.setItem(PLAYER_NAME_STORAGE_KEY, sanitizePlayerName(name).slice(0, 8));
}

export function getPlayerNameValidationMessage(rawName: string): string | null {
  const trimmed = rawName.trim();
  if (!trimmed) {
    return 'leaderboardModal.errorEmpty';
  }

  if (trimmed.length > 8) {
    return 'leaderboardModal.errorTooLong';
  }

  const sanitized = sanitizePlayerName(trimmed);
  if (!sanitized) {
    return 'leaderboardModal.errorInvalidChars';
  }

  if (!isValidPlayerName(sanitized)) {
    return 'leaderboardModal.errorRange';
  }

  return null;
}

function normalizePlayerName(name: string): string {
  return name.toLowerCase();
}

export async function submitGameHighscore(
  gameId: LeaderboardGameId,
  playerName: string,
  score: number
): Promise<boolean> {
  if (!firestoreDb || !hasFirebaseConfig || !Number.isFinite(score) || score <= 0) {
    return false;
  }

  const name = sanitizePlayerName(playerName);
  if (!isValidPlayerName(name)) {
    return false;
  }

  savePlayerName(name);

  const normalizedName = normalizePlayerName(name);
  const scoreDocRef = doc(firestoreDb, 'leaderboards', gameId, 'scores', normalizedName);

  await runTransaction(firestoreDb, async (transaction) => {
    const existing = await transaction.get(scoreDocRef);

    if (!existing.exists()) {
      transaction.set(scoreDocRef, {
        name,
        normalizedName,
        score,
        updatedAt: serverTimestamp(),
      });
      return;
    }

    const currentData = existing.data();
    const currentScore = typeof currentData.score === 'number' ? currentData.score : 0;

    if (score > currentScore) {
      transaction.update(scoreDocRef, {
        name,
        score,
        updatedAt: serverTimestamp(),
      });
    }
  });

  return true;
}

export async function fetchTopScoresForGame(
  gameId: LeaderboardGameId,
  maxEntries = 3
): Promise<LeaderboardEntry[]> {
  if (!firestoreDb || !hasFirebaseConfig) {
    return [];
  }

  const scoresRef = collection(firestoreDb, 'leaderboards', gameId, 'scores');
  const q = query(scoresRef, orderBy('score', 'desc'), limit(maxEntries));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((item) => {
      const data = item.data();
      if (typeof data.name !== 'string' || typeof data.score !== 'number') {
        return null;
      }
      return {
        name: data.name,
        score: data.score,
      } as LeaderboardEntry;
    })
    .filter((item): item is LeaderboardEntry => item !== null);
}

export async function fetchTopScoresForAllGames(
  gameIds: LeaderboardGameId[],
  maxEntries = 3
): Promise<Record<LeaderboardGameId, LeaderboardEntry[]>> {
  const initial = gameIds.reduce((acc, gameId) => {
    acc[gameId] = [];
    return acc;
  }, {} as Record<LeaderboardGameId, LeaderboardEntry[]>);

  if (!hasFirebaseConfig) {
    return initial;
  }

  const entries = await Promise.all(
    gameIds.map(async (gameId) => {
      const scores = await fetchTopScoresForGame(gameId, maxEntries);
      return { gameId, scores };
    })
  );

  entries.forEach(({ gameId, scores }) => {
    initial[gameId] = scores;
  });

  return initial;
}
